import React, { useRef, useEffect, useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

const LineChart = ({ activeDataset, onDatasetChange, options }) => {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState({
    datasets: [],
  });


  const {account, deposit, withdrawal, transactions} = options


  const groupLogByMonths = (deposits) => {
    return deposits?.reduce((dep, event) => {
      const eventDate = new Date(event?.transactionValueDate);
      const month = eventDate.toLocaleString('default', { month: 'long' }); // Get full month name (e.g., "November")
      
      if (!dep[month]) {
        dep[month] = [];
      }
      dep[month].push(event);
      return dep;
    }, {});
  };
  
  const dep = groupLogByMonths(deposit);
  const wit = groupLogByMonths(withdrawal);


  const getTransValue = (arr, key) => {
    return arr?.reduce((acc, obj) => {
      return acc + (obj[key] || 0); // Sum the value of the key, defaulting to 0 if not present
    }, 0);
  }

  // get chat months
  let months = []
  function formatDate(date) {

    date = new Date(date);
    var monthIndex = date.toLocaleString('default', { month: 'long' });

    return monthIndex 
}

  for ( let i=11; i>=0; i--) {
      let now = new Date();
      let newdate = now.setMonth(now.getMonth() - i);
      // console.log(formatDate(newdate ));
      months.push(formatDate(newdate))
  }

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      interaction: {
        intersect: false,
        mode: "index",
      },
      scales: {
        y: {
          grid: {
            drawBorder: false,
            display: true,
            drawOnChartArea: true,
            drawTicks: false,
            borderDash: [5, 5],
            color: "rgba(255, 255, 255, .2)",
          },
          ticks: {
            display: true,
            padding: 12,
            color: "#0D1340",
            font: {
              size: 11,
              family: "Open Sans",
              style: "normal",
              lineHeight: 2,
            },
            callback: function (value) {
              return "$" + value;
            },
          },
          beginAtZero: true,
        },
        x: {
          grid: {
            drawBorder: false,
            display: false,
            drawOnChartArea: false,
            drawTicks: false,
            borderDash: [5, 5],
          },
          ticks: {
            display: true,
            color: "#0D1340",
            padding: 20,
            font: {
              size: 13,
              family: "Open Sans",
              style: "normal",
              lineHeight: 2,
            },
          },
        },
      },
    }),
    []
  );


      // Simulated data - replace with actual data fetching logic
      const getData = () => ({
        income: [ getTransValue(dep?.February, 'creditAmount'), getTransValue(dep?.March, 'creditAmount'), getTransValue(dep?.April, 'creditAmount'), getTransValue(dep?.May, 'creditAmount'), getTransValue(dep?.June, 'creditAmount'),
           getTransValue(dep?.July, 'creditAmount'), getTransValue(dep?.August, 'creditAmount'), getTransValue(dep?.September, 'creditAmount'), getTransValue(dep?.October, 'creditAmount'), getTransValue(dep?.November, 'creditAmount'), getTransValue(dep?.December, 'creditAmount'), getTransValue(dep?.January, 'creditAmount'),
        ],
        expense: [ getTransValue(wit?.February, 'debitAmount'), getTransValue(wit?.March, 'debitAmount'), getTransValue(wit?.April, 'debitAmount'), getTransValue(wit?.May, 'debitAmount'), getTransValue(wit?.June, 'debitAmount'),
           getTransValue(wit?.July, 'debitAmount'), getTransValue(wit?.August, 'debitAmount'), getTransValue(wit?.September, 'debitAmount'), getTransValue(wit?.October, 'debitAmount'), getTransValue(wit?.November, 'debitAmount'), getTransValue(wit?.December, 'debitAmount'), getTransValue(wit?.January, 'debitAmount'),
        ],
      });
      
  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) {
      return;
    }

    const chartContext = chart.canvas.getContext("2d");

    const gradientIncome = chartContext.createLinearGradient(0, 230, 0, 50);
    gradientIncome.addColorStop(1, "rgba(14, 60, 110, 0.2)");
    gradientIncome.addColorStop(0.2, "rgba(27, 93, 150, 0.0)");
    gradientIncome.addColorStop(0, "rgba(27, 93, 150, 0)");

    const gradientExpense = chartContext.createLinearGradient(0, 230, 0, 50);
    gradientExpense.addColorStop(1, "rgba(178, 178, 178, 0.2)");
    gradientExpense.addColorStop(0.2, "rgba(116, 123, 124, 0.0)");
    gradientExpense.addColorStop(0, "rgba(116, 123, 124, 0)");



    const { income, expense } = getData();
    const maxValue = Math.max(...income, ...expense);

    setChartData({
      labels: months,
      datasets: [
        {
          label: "Deposits",
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 0,
          borderColor: "#0E3C6E",
          backgroundColor: gradientIncome,
          fill: true,
          data: income,
          maxBarThickness: 6,
          hidden: activeDataset === "expense",
        },
        {
          label: "Withdrawals",
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 0,
          borderColor: "#B2B2B2",
          backgroundColor: gradientExpense,
          fill: true,
          data: expense,
          maxBarThickness: 6,
          hidden: activeDataset === "income",
        },
      ],
    });

    chart.options.scales.y.max = Math.ceil(maxValue / 1000) * 1000; // Round up to nearest thousand
    chart.update();
  }, [activeDataset, chartOptions, getData().expense, getData().income]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-end mb-4">
        <button
          className={`px-3 py-1 mr-2 text-xs rounded ${
            activeDataset === "both"
              ? "bg-brand-4 text-white"
              : "bg-brand-1 text-white"
          }`}
          onClick={() => onDatasetChange("both")}
        >
          Both
        </button>
        <button
          className={`px-3 py-1 mr-2 text-xs rounded ${
            activeDataset === "income"
              ? "bg-brand-4 text-white"
              : "bg-brand-1 text-white"
          }`}
          onClick={() => onDatasetChange("income")}
        >
          Deposits
        </button>
        <button
          className={`px-3 py-1 text-xs rounded ${
            activeDataset === "expense"
              ? "bg-brand-4 text-white"
              : "bg-brand-1 text-white"
          }`}
          onClick={() => onDatasetChange("expense")}
        >
          Withdrawals
        </button>
      </div>
      <div className="flex-grow">
        <Line ref={chartRef} data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default LineChart;
